import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ChevronRight, ChevronLeft, Plus, Trash2, Save, X, FileDown
} from 'lucide-react';
import { customMethodsApi } from '../services/api';
import { useToast } from '../context/ToastContext';
import './CustomMethodWizard.css';

interface MethodStep {
  id: string;
  step: number;
  text: string;
}

interface CustomSection {
  id: string;
  name: string;
  content: string;
}

interface FormData {
  // Schritt 1
  name: string;
  description: string;
  category_id: string;
  // Schritt 2
  duration_minutes: number | null;
  duration_max_minutes: number | null;
  min_participants: number | null;
  max_participants: number | null;
  difficulty_level: 'beginner' | 'intermediate' | 'advanced';
  // Schritt 3
  benefit: string;
  instructions: MethodStep[];
  materials: string;
  debriefing: string;
  theory_background: string;
  // Schritt 4
  custom_sections: CustomSection[];
}

const initialForm: FormData = {
  name: '',
  description: '',
  category_id: '',
  duration_minutes: null,
  duration_max_minutes: null,
  min_participants: null,
  max_participants: null,
  difficulty_level: 'beginner',
  benefit: '',
  instructions: [],
  materials: '',
  debriefing: '',
  theory_background: '',
  custom_sections: [],
};

export default function CustomMethodWizard() {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [step, setStep] = useState(1);
  const [form, setForm] = useState<FormData>(initialForm);
  const [categories, setCategories] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [newStepText, setNewStepText] = useState('');
  const [newSectionName, setNewSectionName] = useState('');
  const [newSectionContent, setNewSectionContent] = useState('');

  // Load categories on mount
  React.useEffect(() => {
    // Placeholder: würde von API kommen
    setCategories([
      { id: 'cat1', name: '1:1 Coaching' },
      { id: 'cat2', name: 'Systemisch' },
      { id: 'cat3', name: 'Lösungsorientiert' },
      { id: 'cat4', name: 'Team' },
      { id: 'cat5', name: 'Reflexion' },
    ]);
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    const numFields = ['duration_minutes', 'duration_max_minutes', 'min_participants', 'max_participants'];
    setForm(prev => ({
      ...prev,
      [name]: numFields.includes(name) ? (value ? parseInt(value) : null) : value,
    }));
  };

  const addStep = () => {
    if (!newStepText.trim()) return;
    const newStep: MethodStep = {
      id: Math.random().toString(36),
      step: form.instructions.length + 1,
      text: newStepText,
    };
    setForm(prev => ({
      ...prev,
      instructions: [...prev.instructions, newStep],
    }));
    setNewStepText('');
  };

  const removeStep = (id: string) => {
    setForm(prev => ({
      ...prev,
      instructions: prev.instructions.filter(s => s.id !== id).map((s, i) => ({
        ...s,
        step: i + 1,
      })),
    }));
  };

  const addCustomSection = () => {
    if (!newSectionName.trim() || !newSectionContent.trim()) return;
    const newSection: CustomSection = {
      id: Math.random().toString(36),
      name: newSectionName,
      content: newSectionContent,
    };
    setForm(prev => ({
      ...prev,
      custom_sections: [...prev.custom_sections, newSection],
    }));
    setNewSectionName('');
    setNewSectionContent('');
  };

  const removeCustomSection = (id: string) => {
    setForm(prev => ({
      ...prev,
      custom_sections: prev.custom_sections.filter(s => s.id !== id),
    }));
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      const payload = {
        name: form.name,
        description: form.description,
        category_id: form.category_id || null,
        duration_minutes: form.duration_minutes,
        duration_max_minutes: form.duration_max_minutes,
        min_participants: form.min_participants,
        max_participants: form.max_participants,
        difficulty_level: form.difficulty_level,
        benefit: form.benefit,
        instructions: form.instructions.map(s => ({
          step: s.step,
          text: s.text,
        })),
        materials: form.materials,
        debriefing: form.debriefing,
        theory_background: form.theory_background,
        custom_sections: form.custom_sections.map(s => ({
          name: s.name,
          content: s.content,
        })),
      };

      await customMethodsApi.create(payload);
      navigate('/methoden');
    } catch (err) {
      showToast('Fehler beim Speichern der Methode', 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="cmw-root">
      {/* ── Toolbar ── */}
      <div className="cmw-toolbar no-print">
        <button className="cmw-btn-back" onClick={() => navigate('/methoden')}>
          ← Abbrechen
        </button>
        <div className="cmw-progress">
          <span className="cmw-step-indicator">Schritt {step} von 4</span>
          <div className="cmw-progress-bar">
            <div className="cmw-progress-fill" style={{ width: `${(step / 4) * 100}%` }}></div>
          </div>
        </div>
        <div className="cmw-toolbar-right">
          {step > 1 && (
            <button className="cmw-btn-nav" onClick={() => setStep(step - 1)}>
              <ChevronLeft size={16} /> Zurück
            </button>
          )}
          {step < 4 && (
            <button className="cmw-btn-nav" onClick={() => setStep(step + 1)}>
              Weiter <ChevronRight size={16} />
            </button>
          )}
          {step === 4 && (
            <button className="cmw-btn-save" onClick={handleSave} disabled={loading}>
              <Save size={16} /> {loading ? 'Speichert…' : 'Speichern'}
            </button>
          )}
        </div>
      </div>

      {/* ── Content ── */}
      <div className="cmw-container">
        {/* ═══ SCHRITT 1: Basis-Infos ═══ */}
        {step === 1 && (
          <div className="cmw-step-content">
            <div className="cmw-step-header">
              <h2>Schritt 1: Basis-Informationen</h2>
              <p>Geben Sie Namen, Kategorie und grundlegende Parameter ein.</p>
            </div>

            <div className="cmw-form-group">
              <label>Methodenname *</label>
              <input
                type="text"
                name="name"
                value={form.name}
                onChange={handleInputChange}
                placeholder="z.B. Meine spezielle Visualisierungs-Methode"
                className="cmw-input"
              />
            </div>

            <div className="cmw-form-group">
              <label>Kurzbeschreibung</label>
              <textarea
                name="description"
                value={form.description}
                onChange={handleInputChange}
                placeholder="Kurz beschreiben, worum es bei dieser Methode geht…"
                className="cmw-textarea"
                rows={3}
              ></textarea>
            </div>

            <div className="cmw-form-row">
              <div className="cmw-form-group">
                <label>Kategorie</label>
                <select
                  name="category_id"
                  value={form.category_id}
                  onChange={handleInputChange}
                  className="cmw-select"
                >
                  <option value="">-- Wählen --</option>
                  {categories.map(cat => (
                    <option key={cat.id} value={cat.id}>{cat.name}</option>
                  ))}
                </select>
              </div>

              <div className="cmw-form-group">
                <label>Schwierigkeitsgrad</label>
                <select
                  name="difficulty_level"
                  value={form.difficulty_level}
                  onChange={handleInputChange}
                  className="cmw-select"
                >
                  <option value="beginner">Einsteiger</option>
                  <option value="intermediate">Fortgeschritten</option>
                  <option value="advanced">Experte</option>
                </select>
              </div>
            </div>

            <div className="cmw-form-row">
              <div className="cmw-form-group">
                <label>Dauer (Min.)</label>
                <input
                  type="number"
                  name="duration_minutes"
                  value={form.duration_minutes || ''}
                  onChange={handleInputChange}
                  placeholder="z.B. 30"
                  className="cmw-input"
                  min="1"
                />
              </div>
              <div className="cmw-form-group">
                <label>Max. Dauer (Min.)</label>
                <input
                  type="number"
                  name="duration_max_minutes"
                  value={form.duration_max_minutes || ''}
                  onChange={handleInputChange}
                  placeholder="z.B. 60"
                  className="cmw-input"
                  min="1"
                />
              </div>
            </div>

            <div className="cmw-form-row">
              <div className="cmw-form-group">
                <label>Min. Teilnehmer</label>
                <input
                  type="number"
                  name="min_participants"
                  value={form.min_participants || ''}
                  onChange={handleInputChange}
                  placeholder="z.B. 1"
                  className="cmw-input"
                  min="1"
                />
              </div>
              <div className="cmw-form-group">
                <label>Max. Teilnehmer</label>
                <input
                  type="number"
                  name="max_participants"
                  value={form.max_participants || ''}
                  onChange={handleInputChange}
                  placeholder="z.B. 8 (leer = unbegrenzt)"
                  className="cmw-input"
                  min="1"
                />
              </div>
            </div>
          </div>
        )}

        {/* ═══ SCHRITT 2: Ablauf-Schritte ═══ */}
        {step === 2 && (
          <div className="cmw-step-content">
            <div className="cmw-step-header">
              <h2>Schritt 2: Ablauf der Methode</h2>
              <p>Definieren Sie die Einzelschritte der Methoden-Durchführung.</p>
            </div>

            <div className="cmw-steps-container">
              {form.instructions.length > 0 ? (
                <ol className="cmw-steps-list">
                  {form.instructions.map((step, idx) => (
                    <li key={step.id} className="cmw-step-item">
                      <span className="cmw-step-num">{idx + 1}</span>
                      <div className="cmw-step-content-inner">
                        <p>{step.text}</p>
                      </div>
                      <button
                        className="cmw-btn-remove"
                        onClick={() => removeStep(step.id)}
                        title="Schritt löschen"
                      >
                        <Trash2 size={16} />
                      </button>
                    </li>
                  ))}
                </ol>
              ) : (
                <div className="cmw-empty-state">
                  <p>Keine Schritte hinzugefügt. Fügen Sie die ersten Schritte ein!</p>
                </div>
              )}
            </div>

            <div className="cmw-form-group">
              <label>Neuen Schritt hinzufügen</label>
              <textarea
                value={newStepText}
                onChange={(e) => setNewStepText(e.target.value)}
                placeholder="Beschreiben Sie einen Ablauf-Schritt…"
                className="cmw-textarea"
                rows={3}
              ></textarea>
              <button
                className="cmw-btn-primary"
                onClick={addStep}
                disabled={!newStepText.trim()}
              >
                <Plus size={16} /> Schritt hinzufügen
              </button>
            </div>
          </div>
        )}

        {/* ═══ SCHRITT 3: Standard-Felder ═══ */}
        {step === 3 && (
          <div className="cmw-step-content">
            <div className="cmw-step-header">
              <h2>Schritt 3: Inhalt & Kontext</h2>
              <p>Nutzen, Materialien, Nachbesprechung und Theoriehintergrund.</p>
            </div>

            <div className="cmw-form-group">
              <label>Nutzen &amp; Einsatzgebiet</label>
              <textarea
                name="benefit"
                value={form.benefit}
                onChange={handleInputChange}
                placeholder="Wann und wofür eignet sich diese Methode? Was sind die Vorteile?"
                className="cmw-textarea"
                rows={4}
              ></textarea>
            </div>

            <div className="cmw-form-group">
              <label>Benötigte Materialien</label>
              <textarea
                name="materials"
                value={form.materials}
                onChange={handleInputChange}
                placeholder="z.B. Flipchart, Moderationskarten, Whiteboard…"
                className="cmw-textarea"
                rows={3}
              ></textarea>
            </div>

            <div className="cmw-form-group">
              <label>Nachbesprechung (Debriefing)</label>
              <textarea
                name="debriefing"
                value={form.debriefing}
                onChange={handleInputChange}
                placeholder="Wie wird die Methode reflektiert und ausgewertet? Welche Fragen stellen?"
                className="cmw-textarea"
                rows={4}
              ></textarea>
            </div>

            <div className="cmw-form-group">
              <label>Theoriehintergrund</label>
              <textarea
                name="theory_background"
                value={form.theory_background}
                onChange={handleInputChange}
                placeholder="Historischer Kontext, Autoren, wissenschaftliche Basis…"
                className="cmw-textarea"
                rows={4}
              ></textarea>
            </div>
          </div>
        )}

        {/* ═══ SCHRITT 4: Optionale Textbausteine ═══ */}
        {step === 4 && (
          <div className="cmw-step-content">
            <div className="cmw-step-header">
              <h2>Schritt 4: Optionale Textbausteine</h2>
              <p>Fügen Sie beliebig viele weitere Inhalte hinzu (z.B. Varianten, Bonus-Tipps, häufige Fragen).</p>
            </div>

            <div className="cmw-custom-sections">
              {form.custom_sections.length > 0 ? (
                form.custom_sections.map((section) => (
                  <div key={section.id} className="cmw-section-card">
                    <div className="cmw-section-header-card">
                      <h4>{section.name}</h4>
                      <button
                        className="cmw-btn-remove"
                        onClick={() => removeCustomSection(section.id)}
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                    <p className="cmw-section-preview">{section.content.substring(0, 100)}…</p>
                  </div>
                ))
              ) : (
                <div className="cmw-empty-state">
                  <p>Keine Textbausteine vorhanden. Fügen Sie welche hinzu!</p>
                </div>
              )}
            </div>

            <div className="cmw-form-group">
              <label>Neue Textbaustelle</label>
              <input
                type="text"
                value={newSectionName}
                onChange={(e) => setNewSectionName(e.target.value)}
                placeholder="z.B. Variante, Bonus-Tipp, Häufige Fragen…"
                className="cmw-input"
              />
            </div>

            <div className="cmw-form-group">
              <label>Inhalt</label>
              <textarea
                value={newSectionContent}
                onChange={(e) => setNewSectionContent(e.target.value)}
                placeholder="Fügen Sie den Inhalt für diese Textbaustelle ein…"
                className="cmw-textarea"
                rows={5}
              ></textarea>
              <button
                className="cmw-btn-primary"
                onClick={addCustomSection}
                disabled={!newSectionName.trim() || !newSectionContent.trim()}
              >
                <Plus size={16} /> Textbaustelle hinzufügen
              </button>
            </div>

            <div className="cmw-summary">
              <h3>Zusammenfassung</h3>
              <p><strong>Methode:</strong> {form.name || '(keine Eingabe)'}</p>
              <p><strong>Kategorie:</strong> {form.category_id ? categories.find(c => c.id === form.category_id)?.name : '(keine Kategorie)'}</p>
              <p><strong>Ablauf-Schritte:</strong> {form.instructions.length}</p>
              <p><strong>Textbausteine:</strong> {form.custom_sections.length}</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
